import type { Address } from 'viem';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { dexOfCoin } from '@/lib/hyperliquid/coins';
import {
  holdsUsdcInSpot,
  type PerpAccountMode,
  type PerpAccountState,
  type PerpActiveAssetData,
  type PerpBbo,
  type PerpBook,
  type PerpFill,
  type PerpMarketCtx,
  type PerpOpenOrder,
  type PerpPosition,
  type PerpSpotState,
  type PerpTrade,
} from '@/lib/hyperliquid/types';

import { mergeFills, mergeTrades } from './utils';

export type BookSigFigs = 2 | 3 | 4 | 5 | null;
export const perpsBookKey = (coin: string, nSigFigs: BookSigFigs) =>
  JSON.stringify([coin, nSigFigs]);
export const perpsUserKey = (user: Address) => user.toLowerCase();

export type PerpsAccountData = {
  accountStates: Record<string, PerpAccountState>;
  /** Open (non-zero) positions across every dex; derived once per clearinghouse event. */
  positions: PerpPosition[];
  spotState: PerpSpotState | null;
  openOrders: PerpOpenOrder[];
  fills: PerpFill[];
};
export const EMPTY_PERPS_ACCOUNT: PerpsAccountData = {
  accountStates: {},
  positions: [],
  spotState: null,
  openOrders: [],
  fills: [],
};
const EMPTY_TRADES: PerpTrade[] = [];

export type PerpsExchangeStore = {
  /** Every market's context: the bulk stream overlaid with `activeCtxByCoin`. */
  ctxByCoin: Record<string, PerpMarketCtx>;
  /** Faster per-market stream for the selected market and held positions; wins over the bulk stream while subscribed. */
  activeCtxByCoin: Record<string, PerpMarketCtx>;
  bookByKey: Record<string, PerpBook>;
  bboByCoin: Record<string, PerpBbo>;
  tradesByCoin: Record<string, PerpTrade[]>;
  accountByUser: Record<string, PerpsAccountData>;
  activeAssetDataByUser: Record<string, Record<string, PerpActiveAssetData>>;
  setContexts: (contexts: Record<string, PerpMarketCtx>) => void;
  setActiveContext: (coin: string, ctx: PerpMarketCtx) => void;
  removeActiveContext: (coin: string) => void;
  clearContexts: () => void;
  setBook: (coin: string, nSigFigs: BookSigFigs, book: PerpBook) => void;
  removeBook: (coin: string, nSigFigs: BookSigFigs) => void;
  setBbo: (coin: string, bbo: PerpBbo) => void;
  removeBbo: (coin: string) => void;
  pushTrades: (coin: string, trades: PerpTrade[]) => void;
  removeTrades: (coin: string) => void;
  setAccountStates: (user: Address, states: [string, PerpAccountState][]) => void;
  setSpotState: (user: Address, spotState: PerpSpotState) => void;
  setOpenOrders: (user: Address, dex: string, orders: PerpOpenOrder[]) => void;
  pushFills: (user: Address, fills: PerpFill[]) => void;
  setActiveAssetData: (user: Address, coin: string, data: PerpActiveAssetData) => void;
  removeActiveAssetData: (user: Address, coin: string) => void;
  removeAccount: (user: Address) => void;
};

/** WebSocket snapshots are owned by the subscribed market, precision, or wallet. */
export const usePerpsExchangeStore = createWithEqualityFn(
  immer<PerpsExchangeStore>((set) => ({
    ctxByCoin: {},
    activeCtxByCoin: {},
    bookByKey: {},
    bboByCoin: {},
    tradesByCoin: {},
    accountByUser: {},
    activeAssetDataByUser: {},
    setContexts: (contexts) =>
      set((state) => {
        state.ctxByCoin = { ...contexts, ...state.activeCtxByCoin };
      }),
    setActiveContext: (coin, ctx) =>
      set((state) => {
        state.ctxByCoin[coin] = ctx;
        state.activeCtxByCoin[coin] = ctx;
      }),
    removeActiveContext: (coin) =>
      set((state) => {
        delete state.activeCtxByCoin[coin];
      }),
    clearContexts: () => set({ ctxByCoin: {}, activeCtxByCoin: {} }),
    setBook: (coin, nSigFigs, book) =>
      set((state) => {
        const key = perpsBookKey(coin, nSigFigs);
        if (!state.bookByKey[key] || state.bookByKey[key].time <= book.time)
          state.bookByKey[key] = book;
      }),
    removeBook: (coin, nSigFigs) =>
      set((state) => {
        delete state.bookByKey[perpsBookKey(coin, nSigFigs)];
      }),
    setBbo: (coin, bbo) =>
      set((state) => {
        state.bboByCoin[coin] = bbo;
      }),
    removeBbo: (coin) =>
      set((state) => {
        delete state.bboByCoin[coin];
      }),
    pushTrades: (coin, trades) =>
      set((state) => {
        state.tradesByCoin[coin] = mergeTrades(state.tradesByCoin[coin], trades);
      }),
    removeTrades: (coin) =>
      set((state) => {
        delete state.tradesByCoin[coin];
      }),
    setAccountStates: (user, states) =>
      set((state) => {
        const key = perpsUserKey(user);
        state.accountByUser[key] = {
          ...(state.accountByUser[key] ?? EMPTY_PERPS_ACCOUNT),
          accountStates: Object.fromEntries(states),
          positions: states
            .flatMap(([, dexState]) => dexState.assetPositions.map(({ position }) => position))
            .filter((position) => Number(position.szi) !== 0),
        };
      }),
    setSpotState: (user, spotState) =>
      set((state) => {
        const key = perpsUserKey(user);
        state.accountByUser[key] = {
          ...(state.accountByUser[key] ?? EMPTY_PERPS_ACCOUNT),
          spotState,
        };
      }),
    setOpenOrders: (user, dex, orders) =>
      set((state) => {
        const key = perpsUserKey(user);
        const account = state.accountByUser[key] ?? EMPTY_PERPS_ACCOUNT;
        state.accountByUser[key] = {
          ...account,
          openOrders: account.openOrders
            .filter((order) => dexOfCoin(order.coin) !== dex)
            .concat(orders),
        };
      }),
    pushFills: (user, fills) =>
      set((state) => {
        const key = perpsUserKey(user);
        const account = state.accountByUser[key] ?? EMPTY_PERPS_ACCOUNT;
        state.accountByUser[key] = { ...account, fills: mergeFills(account.fills, fills) };
      }),
    setActiveAssetData: (user, coin, data) =>
      set((state) => {
        const key = perpsUserKey(user);
        state.activeAssetDataByUser[key] ??= {};
        state.activeAssetDataByUser[key][coin] = data;
      }),
    removeActiveAssetData: (user, coin) =>
      set((state) => {
        const data = state.activeAssetDataByUser[perpsUserKey(user)];
        if (data) delete data[coin];
      }),
    removeAccount: (user) =>
      set((state) => {
        const key = perpsUserKey(user);
        delete state.accountByUser[key];
        delete state.activeAssetDataByUser[key];
      }),
  })),
  shallow,
);

export const perpsAccountSelector = (user: Address | undefined) => (state: PerpsExchangeStore) =>
  (user ? state.accountByUser[perpsUserKey(user)] : undefined) ?? EMPTY_PERPS_ACCOUNT;
export const perpsActiveAssetDataSelector =
  (user: Address | undefined, coin: string) => (state: PerpsExchangeStore) =>
    (user ? state.activeAssetDataByUser[perpsUserKey(user)]?.[coin] : undefined) ?? null;
export const perpsBookSelector =
  (coin: string, nSigFigs: BookSigFigs) => (state: PerpsExchangeStore) =>
    state.bookByKey[perpsBookKey(coin, nSigFigs)];
export const perpsBboSelector = (coin: string) => (state: PerpsExchangeStore) =>
  state.bboByCoin[coin];
export const perpsTradesSelector = (coin: string) => (state: PerpsExchangeStore) =>
  state.tradesByCoin[coin] ?? EMPTY_TRADES;
export const perpsContextSelector = (coin: string) => (state: PerpsExchangeStore) =>
  state.ctxByCoin[coin];
export const perpsContextsSelector = (state: PerpsExchangeStore) => state.ctxByCoin;

export const spotUsdc = (account: PerpsAccountData) =>
  account.spotState?.balances.find((balance) => balance.coin === 'USDC');

/**
 * Equity and withdrawable USDC from whichever clearinghouse holds them in the user's account
 * mode. `accountMode` is REST data (see `accountModeQueryOptions`), so it is passed in.
 */
export function accountBalances(account: PerpsAccountData, accountMode: PerpAccountMode | null) {
  if (accountMode && holdsUsdcInSpot(accountMode)) {
    if (!account.spotState) return undefined;
    const usdc = spotUsdc(account);
    const total = Number(usdc?.total ?? 0);
    return { equity: total, withdrawable: total - Number(usdc?.hold ?? 0) };
  }
  const mainDex = account.accountStates[''];
  if (!accountMode || !mainDex) return undefined;
  return {
    equity: Number(mainDex.marginSummary.accountValue),
    withdrawable: Number(mainDex.withdrawable),
  };
}

/**
 * Collateral and maintenance margin of the cross positions that share margin with `coin`:
 * its own dex in standard mode; every (USDC) dex in unified and portfolio-margin modes,
 * where per-dex balances are not meaningful and spot USDC net of isolated margin is the equity.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/account-abstraction-modes
 */
export function crossMargin(
  account: PerpsAccountData,
  accountMode: PerpAccountMode | null,
  coin: string,
) {
  // ponytail: portfolio margin also counts HYPE/BTC collateral, borrows, a 20 USDC offset, and
  // liquidates at a 0.95 ratio; model its portfolio margin ratio if estimates need to be exact.
  if (accountMode && holdsUsdcInSpot(accountMode)) {
    if (!account.spotState) return undefined;
    const isolatedMargin = account.positions
      .filter((position) => position.leverage.type === 'isolated')
      .reduce((sum, position) => sum + Number(position.marginUsed), 0);
    return {
      accountValue: Number(spotUsdc(account)?.total ?? 0) - isolatedMargin,
      maintenanceMarginUsed: Object.values(account.accountStates).reduce(
        (sum, dexState) => sum + Number(dexState.crossMaintenanceMarginUsed),
        0,
      ),
    };
  }
  const dexState = account.accountStates[dexOfCoin(coin)];
  if (!accountMode || !dexState) return undefined;
  return {
    accountValue: Number(dexState.crossMarginSummary.accountValue),
    maintenanceMarginUsed: Number(dexState.crossMaintenanceMarginUsed),
  };
}
